import { ChevronDown, Loader2, Pencil } from 'lucide-react';
import React, { useEffect, useId, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { useTokenPairStore } from '@/features/tokenPairs';
import type { UpdateTriggersRequest } from '@/lib/api/speed';
import { cn } from '@/lib/utils';

import { useOpenTradeForCoin } from '../hooks/useOpenTradeForCoin';
import { useUpdateTriggers } from '../hooks/useUpdateTriggers';
import { selectIsRealMode, useTradingStore } from '../store';

interface PositionTriggerEditorProps {
    /** Live mark price, already threaded into both hosts as `currentValue`. */
    currentValue: number;
    /** Mobile host — tighter spacing, same controls. */
    compact?: boolean;
}

/**
 * Canonical text form of a trigger price, used both for display and for the
 * "did anything actually change" comparison, so `'103.40'` and `'103.4'` are
 * never mistaken for an edit. `null`/absent/unparseable → `''` (the leg is off).
 * Sub-cent prices are spelled out rather than allowed to reach exponent
 * notation, which the backend would not accept back.
 */
function normalizeTrigger(raw: string | null | undefined): string {
    if (raw === null || raw === undefined || raw.trim() === '') return '';
    const value = Number(raw);
    if (!Number.isFinite(value) || value <= 0) return '';
    if (value < 1e-4) return value.toFixed(12).replace(/0+$/, '');
    return String(value);
}

/**
 * Post-open TP/SL editor for the live position. Shows the trade's ACTUAL
 * installed triggers (from the active-trades query, i.e. what Hyperliquid is
 * really resting) and lets the user retarget or remove either leg.
 *
 * Real mode only, and only while an Open trade exists for the active coin —
 * demo positions have no backend triggers to edit. Each leg is tri-state on the
 * wire: untouched legs are omitted, a cleared leg is sent as `null` (cancel),
 * and a retargeted leg is sent as the typed decimal string (the backend rounds
 * to Hyperliquid's tick rules and echoes the final price back, which is why the
 * editor collapses on success and re-reads the refetched trade rather than
 * displaying the draft).
 */
const PositionTriggerEditor: React.FC<PositionTriggerEditorProps> = ({
    currentValue,
    compact,
}) => {
    const isRealMode = useTradingStore(selectIsRealMode);
    const selectedCoin = useTokenPairStore((s) => s.selectedCoin);
    const { mutate, isPending } = useUpdateTriggers();

    // same resolution order the close path uses (useRealTradeActions): a
    // recovered trade's coin wins over whatever market is currently selected.
    // TP/SL belong to the charted market's position — the rail follows the chart.
    const coin = selectedCoin;
    // Same resolver the chart's TP/SL lines read, so the two can't disagree
    // about the position they are both describing.
    const trade = useOpenTradeForCoin(coin);

    const currentTp = normalizeTrigger(trade?.takeProfit);
    const currentSl = normalizeTrigger(trade?.stopLoss);

    const [expanded, setExpanded] = useState(false);
    const [tpDraft, setTpDraft] = useState(currentTp);
    const [slDraft, setSlDraft] = useState(currentSl);
    const fieldId = useId();

    // Re-seed the drafts from the trade while the editor is closed (a fill, a
    // fired leg or another tab's edit all arrive through the trades query). Once
    // it's open the user owns the fields — a background refetch must not
    // overwrite what they're typing.
    useEffect(() => {
        if (expanded) return;
        setTpDraft(currentTp);
        setSlDraft(currentSl);
    }, [expanded, currentTp, currentSl]);

    const mark =
        Number.isFinite(currentValue) && currentValue > 0 ? currentValue : null;
    const isLong = trade?.isLong ?? true;

    // An empty field is valid — it means "remove this leg". Anything typed has
    // to be a positive number on the correct side of the live mark, which is the
    // same rule Hyperliquid enforces when the trigger is placed.
    const validationError = useMemo(() => {
        const check = (raw: string, label: string, above: boolean) => {
            const text = raw.trim();
            if (text === '') return null;
            const value = Number(text);
            if (!Number.isFinite(value) || value <= 0) {
                return `${label} must be a positive price.`;
            }
            if (mark === null) return null;
            if (above && value <= mark) {
                return `${label} must be above the mark price.`;
            }
            if (!above && value >= mark) {
                return `${label} must be below the mark price.`;
            }
            return null;
        };
        return (
            check(tpDraft, 'Take profit', isLong) ??
            check(slDraft, 'Stop loss', !isLong)
        );
    }, [tpDraft, slDraft, isLong, mark]);

    const tpChanged = normalizeTrigger(tpDraft) !== currentTp;
    const slChanged = normalizeTrigger(slDraft) !== currentSl;
    const hasChanges = tpChanged || slChanged;

    // Demo positions and the no-open-trade case have nothing to edit. Placed
    // after the hooks so the hook order stays stable across that transition.
    if (!isRealMode || !trade || !coin) return null;

    const applyChanges = () => {
        if (!hasChanges || validationError !== null || isPending) return;
        // Tri-state per leg: omit what didn't change, `null` to cancel a leg the
        // user cleared, the raw typed string for a retarget.
        const body: UpdateTriggersRequest = { coin };
        if (tpChanged) {
            const text = tpDraft.trim();
            body.takeProfit = text === '' ? null : text;
        }
        if (slChanged) {
            const text = slDraft.trim();
            body.stopLoss = text === '' ? null : text;
        }
        mutate(body, { onSuccess: () => setExpanded(false) });
    };

    const cancelEdit = () => {
        setTpDraft(currentTp);
        setSlDraft(currentSl);
        setExpanded(false);
    };

    const inputClass =
        'w-full rounded-md border border-border bg-background/40 px-2 py-1.5 font-mono text-[11px] tabular-nums transition-colors focus:outline-none focus:ring-2 focus:ring-ring/40 disabled:opacity-50';
    const labelClass =
        'text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground';

    return (
        <div className={cn('flex flex-col', compact ? 'gap-1.5' : 'gap-2')}>
            <button
                type="button"
                onClick={() => setExpanded((prev) => !prev)}
                aria-expanded={expanded}
                className="flex w-full cursor-pointer items-center gap-2 rounded-lg border border-border/60 bg-secondary/30 px-2.5 py-2 text-left transition-colors hover:bg-secondary/60"
            >
                <Pencil size={11} className="shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate font-mono text-[10px] tabular-nums text-muted-foreground">
                    TP{' '}
                    <span
                        className={cn(
                            'font-bold',
                            currentTp !== ''
                                ? 'text-primary'
                                : 'text-foreground',
                        )}
                    >
                        {currentTp === '' ? 'Off' : currentTp}
                    </span>
                    {' · '}SL{' '}
                    <span
                        className={cn(
                            'font-bold',
                            currentSl !== ''
                                ? 'text-destructive'
                                : 'text-foreground',
                        )}
                    >
                        {currentSl === '' ? 'Off' : currentSl}
                    </span>
                </span>
                <span className="shrink-0 text-[9px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    Edit
                </span>
                <ChevronDown
                    size={12}
                    className={cn(
                        'shrink-0 text-muted-foreground transition-transform duration-200',
                        expanded && 'rotate-180',
                    )}
                />
            </button>

            {expanded && (
                <div className="flex flex-col gap-2 rounded-xl border border-border bg-secondary/30 p-3">
                    <div className="grid grid-cols-2 gap-2">
                        <div className="flex flex-col gap-1">
                            <label
                                htmlFor={`${fieldId}-tp`}
                                className={labelClass}
                            >
                                Take profit
                            </label>
                            <input
                                id={`${fieldId}-tp`}
                                type="number"
                                inputMode="decimal"
                                step="any"
                                min="0"
                                placeholder="Off"
                                disabled={isPending}
                                value={tpDraft}
                                onChange={(e) => setTpDraft(e.target.value)}
                                className={inputClass}
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label
                                htmlFor={`${fieldId}-sl`}
                                className={labelClass}
                            >
                                Stop loss
                            </label>
                            <input
                                id={`${fieldId}-sl`}
                                type="number"
                                inputMode="decimal"
                                step="any"
                                min="0"
                                placeholder="Off"
                                disabled={isPending}
                                value={slDraft}
                                onChange={(e) => setSlDraft(e.target.value)}
                                className={inputClass}
                            />
                        </div>
                    </div>

                    <p className="text-[10px] text-muted-foreground">
                        Leave a field empty to remove that trigger.
                    </p>

                    {validationError !== null && (
                        <p className="text-[10px] text-destructive">
                            {validationError}
                        </p>
                    )}

                    <div className="flex items-center gap-2">
                        <Button
                            size="sm"
                            onClick={applyChanges}
                            disabled={
                                isPending ||
                                !hasChanges ||
                                validationError !== null
                            }
                            className="flex-1"
                        >
                            {isPending ? (
                                <>
                                    <Loader2
                                        size={14}
                                        className="animate-spin"
                                    />
                                    <span>Updating…</span>
                                </>
                            ) : (
                                'Apply'
                            )}
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={cancelEdit}
                            disabled={isPending}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PositionTriggerEditor;
